import React, { useMemo, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type {
  ImportColumnMapping,
  ImportFieldDefinition,
  ImportMappingSpec,
  ImportSetup,
} from "@/model/interface/DataImport";
import { useGetContactListsQuery, useGetDealStagesQuery } from "@/store/rest_api";

interface ImportMappingStepProps {
  setup: ImportSetup;
  mapping: ImportMappingSpec;
  busy: boolean;
  saveTemplate: boolean;
  templateName: string;
  onMappingChange: (mapping: ImportMappingSpec) => void;
  onSheetChange: (sheetName: string) => void;
  onSaveTemplateChange: (saveTemplate: boolean) => void;
  onTemplateNameChange: (templateName: string) => void;
}

const NO_LIST = "none";
const SAMPLE_COUNT = 3;

export default function ImportMappingStep({
  setup,
  mapping,
  busy,
  saveTemplate,
  templateName,
  onMappingChange,
  onSheetChange,
  onSaveTemplateChange,
  onTemplateNameChange,
}: ImportMappingStepProps) {
  const isPeopleImport = setup.entityType === "CONTACT" || setup.entityType === "PROPERTY";
  const [tagText, setTagText] = useState(mapping.tagNames.join(", "));
  const { data: listsPage } = useGetContactListsQuery({ page: 0, size: 200 }, { skip: !isPeopleImport });
  const { data: stages = [] } = useGetDealStagesQuery({}, { skip: !isPeopleImport });

  const fallbackField = isPeopleImport ? "custom" : "skip";

  const fieldById = useMemo(
    () => new Map(setup.fields.map((field) => [field.id, field] as const)),
    [setup.fields],
  );

  const groups = useMemo(() => {
    const grouped = new Map<string, ImportFieldDefinition[]>();
    setup.fields.forEach((field) => {
      grouped.set(field.group, [...(grouped.get(field.group) ?? []), field]);
    });
    return Array.from(grouped.entries());
  }, [setup.fields]);

  const samples = useMemo(
    () =>
      setup.headers.map((_header, index) =>
        setup.previewRows
          .map((row) => row[index])
          .filter((value): value is string => Boolean(value))
          .slice(0, SAMPLE_COUNT)
          .join(" · "),
      ),
    [setup.headers, setup.previewRows],
  );

  const mappedFields = Object.values(mapping.columns).map((column) => column.field);
  const needsDealStage =
    isPeopleImport &&
    mappedFields.some((field) => field.startsWith("deal.") && field !== "deal.status") &&
    !mappedFields.includes("deal.status");
  const fallbackStage = stages.find((stage) => !stage.isDefault && !stage.isClosed && stage.isActive) ?? stages[0];

  function columnFor(header: string): ImportColumnMapping {
    return mapping.columns[header] ?? { field: fallbackField, person: 1, slot: 1 };
  }

  function updateColumn(header: string, changes: Partial<ImportColumnMapping>) {
    onMappingChange({
      ...mapping,
      columns: { ...mapping.columns, [header]: { ...columnFor(header), ...changes } },
    });
  }

  function commitTags() {
    const tagNames = tagText
      .split(",")
      .map((tagName) => tagName.trim())
      .filter((tagName) => tagName !== "");
    onMappingChange({ ...mapping, tagNames });
  }

  return (
    <div className="space-y-4">
      {setup.sheetNames.length > 1 && (
        <div className="flex items-center gap-3">
          <Label className="text-sm">Sheet</Label>
          <Select value={setup.sheetName ?? undefined} onValueChange={onSheetChange} disabled={busy}>
            <SelectTrigger className="h-8 w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {setup.sheetNames.map((sheetName) => (
                <SelectItem key={sheetName} value={sheetName}>
                  {sheetName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {setup.templateName && (
        <p className="text-sm text-muted-foreground">
          Columns were matched with your saved mapping &ldquo;{setup.templateName}&rdquo;.
        </p>
      )}

      <div className="max-h-[45vh] overflow-auto rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Column in your file</TableHead>
              <TableHead>Sample values</TableHead>
              <TableHead>Import as</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {setup.headers.map((header, index) => {
              const column = columnFor(header);
              const definition = fieldById.get(column.field);
              return (
                <TableRow key={header}>
                  <TableCell className="font-medium">{header}</TableCell>
                  <TableCell className="max-w-56 truncate text-xs text-muted-foreground" title={samples[index]}>
                    {samples[index] || "Empty in the first rows"}
                  </TableCell>
                  <TableCell>
                    <Select value={column.field} onValueChange={(field) => updateColumn(header, { field })}>
                      <SelectTrigger className="h-8 w-60">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {groups.map(([group, fields]) => (
                          <SelectGroup key={group}>
                            <SelectLabel>{group}</SelectLabel>
                            {fields.map((field) => (
                              <SelectItem key={field.id} value={field.id}>
                                {field.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {definition?.personScoped && isPeopleImport && (
                        <Select
                          value={String(column.person)}
                          onValueChange={(person) => updateColumn(header, { person: Number(person) })}
                        >
                          <SelectTrigger className="h-8 w-28" aria-label="Person">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Person 1</SelectItem>
                            <SelectItem value="2">Person 2</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      {definition?.slotted && (
                        <Input
                          type="number"
                          min={1}
                          max={99}
                          value={column.slot}
                          onChange={(event) => updateColumn(header, { slot: Math.max(1, Number(event.target.value) || 1) })}
                          className="h-8 w-16"
                          aria-label="Phone or email number"
                          title="Phone or email number, for columns like Phone 2 or Email 3"
                        />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {isPeopleImport && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-sm">Add every row to a list</Label>
            <Select
              value={mapping.listIds[0] ?? NO_LIST}
              onValueChange={(listId) => onMappingChange({ ...mapping, listIds: listId === NO_LIST ? [] : [listId] })}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_LIST}>No list</SelectItem>
                {(listsPage?.items ?? []).map((list) => (
                  <SelectItem key={list.id} value={list.id}>
                    {list.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Tag every row</Label>
            <Input
              value={tagText}
              onChange={(event) => setTagText(event.target.value)}
              onBlur={commitTags}
              placeholder="For example: Imported, Probate list"
              className="h-9"
            />
          </div>
        </div>
      )}

      {needsDealStage && (
        <div className="space-y-1.5">
          <Label className="text-sm">Stage for the deals in this file</Label>
          <Select
            value={mapping.dealStageId ?? fallbackStage?.id}
            onValueChange={(dealStageId) => onMappingChange({ ...mapping, dealStageId })}
          >
            <SelectTrigger className="h-9 w-72">
              <SelectValue placeholder="Choose a stage" />
            </SelectTrigger>
            <SelectContent>
              {stages.map((stage) => (
                <SelectItem key={stage.id} value={stage.id}>
                  {stage.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Checkbox
            id="save-import-template"
            checked={saveTemplate}
            onCheckedChange={(checked) => onSaveTemplateChange(checked === true)}
          />
          <Label htmlFor="save-import-template" className="text-sm">
            Save this mapping for files with the same columns
          </Label>
        </div>
        {saveTemplate && (
          <Input
            value={templateName}
            onChange={(event) => onTemplateNameChange(event.target.value)}
            placeholder="Mapping name"
            className="h-8 w-60"
          />
        )}
      </div>
    </div>
  );
}
